import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Send, Loader2, ChevronDown, ChevronUp, Users, Heart, ShieldCheck, Lock, ArrowLeft, CheckCircle2, Circle, Sparkles } from 'lucide-react';
import ReactMarkdown, { Components } from 'react-markdown';
import { useAuth } from '../../context/AuthContext';
import PlanReviewCard from '../../components/chat/PlanReviewCard';
import {
  APPROVE_PLAN_MESSAGE,
  PLAN_REVIEW_FORMAT,
  planEtaRunCopy,
  resolveRunEta,
} from '../../components/chat/planReview';
import type { PlanReviewPayload } from '../../components/chat/planReview';

const API_URL = import.meta.env.VITE_API_URL || '';
const PUBLIC_URL = import.meta.env.VITE_PUBLIC_URL || '';

// Types
interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  experts?: SelectedTwinInfo[];
  plan_review?: PlanReviewPayload; // The editable plan card (content is a markdown fallback)
  format?: string; // PLAN_REVIEW_FORMAT when this message IS the plan card (not an answer)
}

interface SelectedTwinInfo {
  id: number;
  name: string;
  bio?: string;
  tags?: Record<string, string | string[]>;
  reason: string;
}

interface TaskStatus {
  task_id: string;
  state: 'PENDING' | 'PROGRESS' | 'SUCCESS' | 'FAILURE' | 'CANCELLED';
  current?: number;
  total?: number;
  phase?: string;
  message?: string;
  structured_progress?: StructuredProgress;
  result?: {
    success: boolean;
    selected_twins: SelectedTwinInfo[];
    response: string;
    expert_count: number;
    plan_review?: PlanReviewPayload;
  };
  error?: string;
}

// Progress tracking types
interface StageInfo {
  stage: string;
  label: string;
  status: 'pending' | 'active' | 'completed';
}

interface PlanStepProgress {
  id: string;
  label: string;
  status: 'pending' | 'running' | 'completed' | 'skipped';
}

interface StructuredProgress {
  current_stage: string;
  stages: StageInfo[];
  plan_steps?: PlanStepProgress[];
  plan_version: number;
  message: string;
  // Live ETA the backend may attach mid-run; preferred over the approved plan's ETA.
  eta_minutes_min?: number;
  eta_minutes_max?: number;
}

// Custom markdown components for proper rendering
const markdownComponents: Components = {
  h1: ({ children }) => <h1 className="text-xl font-bold text-gray-900 mt-4 mb-2">{children}</h1>,
  h2: ({ children }) => <h2 className="text-lg font-bold text-gray-900 mt-4 mb-2">{children}</h2>,
  h3: ({ children }) => <h3 className="text-base font-semibold text-gray-900 mt-3 mb-1">{children}</h3>,
  h4: ({ children }) => <h4 className="text-sm font-semibold text-gray-900 mt-2 mb-1">{children}</h4>,
  p: ({ children }) => <p className="text-sm text-gray-700 mb-3 leading-relaxed">{children}</p>,
  ul: ({ children }) => <ul className="list-disc list-outside ml-5 mb-3 space-y-1">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal list-outside ml-5 mb-3 space-y-1">{children}</ol>,
  li: ({ children }) => <li className="text-sm text-gray-700 leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-gray-900">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-[#07E5D1] pl-4 py-1 my-3 bg-gray-50 rounded-r-lg">
      {children}
    </blockquote>
  ),
  code: ({ children, className }) => {
    const isInline = !className;
    return isInline ? (
      <code className="bg-gray-100 text-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{children}</code>
    ) : (
      <code className="block bg-gray-900 text-gray-100 p-3 rounded-lg text-xs font-mono overflow-x-auto my-3">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-3">{children}</pre>,
  a: ({ href, children }) => (
    <a href={href} className="text-[#07E5D1] hover:underline" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  hr: () => <hr className="my-4 border-gray-200" />,
};

// Expert Tooltip Component
const ExpertTooltip: React.FC<{
  expert: SelectedTwinInfo;
  children: React.ReactNode;
}> = ({ expert, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const tooltipWidth = 400;
      const margin = 20;
      
      let x = rect.left + rect.width / 2;
      const minX = margin + tooltipWidth / 2;
      const maxX = window.innerWidth - margin - tooltipWidth / 2;
      x = Math.max(minX, Math.min(maxX, x));
      
      setPosition({
        x,
        y: rect.top - 8
      });
    }
    setIsVisible(true);
  };

  const initials = expert.name
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // Format tags for display
  const formatTags = (tags: Record<string, string | string[]> | undefined) => {
    if (!tags) return null;
    const tagItems: string[] = [];
    
    if (tags.overall_experience) {
      tagItems.push(`Experience: ${tags.overall_experience}`);
    }
    if (Array.isArray(tags.key_moments) && tags.key_moments.length > 0) {
      tagItems.push(...tags.key_moments.slice(0, 3));
    }
    
    return tagItems.length > 0 ? tagItems : null;
  };

  const displayTags = formatTags(expert.tags);

  return (
    <div 
      ref={triggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setIsVisible(false)}
      className="relative inline-block"
    >
      {children}
      {isVisible && (
        <div 
          className="fixed transform -translate-x-1/2 -translate-y-full pointer-events-none"
          style={{ left: position.x, top: position.y, zIndex: 9999 }}
        >
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-[300px] max-w-[400px] mb-2">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-[#07E5D1] flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-gray-900">{expert.name}</h4>
                <p className="text-xs text-gray-500 mt-0.5">Labor & Delivery Experience Persona</p>
              </div>
            </div>
            
            {expert.bio && (
              <p className="text-sm text-gray-600 leading-relaxed mb-2">
                {expert.bio.length > 200 ? expert.bio.slice(0, 200) + '...' : expert.bio}
              </p>
            )}
            
            {displayTags && (
              <div className="flex flex-wrap gap-1 mt-2">
                {displayTags.map((tag, idx) => (
                  <span key={idx} className="px-2 py-0.5 bg-[#E8FCFA] text-[#07E5D1] text-xs rounded-full">
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-3 h-3 bg-white border-r border-b border-gray-200 transform rotate-45" />
        </div>
      )}
    </div>
  );
};

// Experts Accordion Component
const ExpertsAccordion: React.FC<{
  experts: SelectedTwinInfo[];
}> = ({ experts }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const visibleCount = 3;
  const hasMore = experts.length > visibleCount;
  const visibleExperts = isExpanded ? experts : experts.slice(0, visibleCount);
  const hiddenCount = experts.length - visibleCount;

  if (experts.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-gray-100">
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-gray-400" />
        <span className="text-xs text-gray-500 font-medium">
          {experts.length} respondent{experts.length !== 1 ? 's' : ''} contributed to this response
        </span>
      </div>
      
      <div className="flex flex-wrap gap-2">
        {visibleExperts.map((expert) => (
          <ExpertTooltip key={expert.id} expert={expert}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-[#07E5D1] hover:bg-[#E8FCFA] transition-all cursor-pointer group">
              <div className="w-5 h-5 rounded-full bg-[#07E5D1] flex items-center justify-center text-white text-[9px] font-medium">
                {expert.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <span className="text-xs text-gray-700 group-hover:text-[#07E5D1] font-medium">
                {expert.name}
              </span>
            </div>
          </ExpertTooltip>
        ))}
        
        {hasMore && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-600 font-medium">Show less</span>
              </>
            ) : (
              <>
                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-600 font-medium">+{hiddenCount} more</span>
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const AskHealthcareChat: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [progressMessage, setProgressMessage] = useState('Finding relevant respondents...');
  const [structuredProgress, setStructuredProgress] = useState<StructuredProgress | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runStartedAtRef = useRef<number | null>(null);

  // Admin check - redirect to login if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, navigate]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent, progressMessage]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Poll for task status
  const pollTaskStatus = useCallback(async (taskId: string) => {
    try {
      // Poll the SAME stream the task was dispatched to (/healthcare-twins/collective).
      // The /healthcare-chat-v2/status serializer drops selected_twins/expert_count, so
      // the respondent-attribution chips never rendered. This endpoint returns them.
      const response = await fetch(`${API_URL}/healthcare-twins/collective/status/${taskId}`, {
        credentials: 'include'
      });
      if (!response.ok) {
        throw new Error('Failed to get task status');
      }
      
      const status: TaskStatus = await response.json();
      
      if (status.state === 'PENDING' || status.state === 'PROGRESS') {
        // Update progress message from backend
        setProgressMessage(status.message || 'Processing...');
        // Update structured progress if available
        if (status.structured_progress) {
          setStructuredProgress(status.structured_progress);
        }
      } else if (status.state === 'SUCCESS' && status.result) {
        // Task completed successfully - only process once (guard against duplicate polls)
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: status.result!.response,
            experts: status.result!.selected_twins,
            plan_review: status.result!.plan_review,
            // A plan_review payload means this message IS the plan card (its content is a
            // markdown fallback of the same plan), so stamp the format the render guard
            // checks. Without a real format field the guard is always-false → card hidden.
            format: status.result!.plan_review ? PLAN_REVIEW_FORMAT : undefined,
          }]);
          
          setIsLoading(false);
          setProgressMessage('Finding relevant respondents...');
          setStructuredProgress(null);
        }
      } else if (status.state === 'FAILURE') {
        // Task failed - only process once
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;

          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `Sorry, I encountered an error: ${status.error || 'Unknown error'}. Please try again.`
          }]);

          setIsLoading(false);
          setProgressMessage('Finding relevant respondents...');
          setStructuredProgress(null);
        }
      } else if (status.state === 'CANCELLED') {
        // Task was cancelled - only process once
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setStructuredProgress(null);
          
          setIsLoading(false);
          setProgressMessage('Finding relevant respondents...');
        }
      }
    } catch (error) {
      console.error('Error polling task status:', error);
    }
  }, []);

  // `overrideText` lets the plan-review card send programmatic messages
  // ("go" on approve, or the composed change description) through the SAME
  // path as a typed message.
  const handleSend = useCallback(async (overrideText?: string) => {
    const text = (overrideText ?? input).trim();
    if (!text || isLoading) return;
    if (overrideText === undefined) setApprovedPlanReview(null);

    const userMessage: ChatMessage = { role: 'user', content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    if (overrideText === undefined) setInput(''); // keep any typed draft on card-driven sends
    setIsLoading(true);
    setStreamingContent('');
    setProgressMessage('Finding relevant respondents...');
    setStructuredProgress(null);

    try {
      const requestBody = {
        query: text,
        messages: newMessages.slice(0, -1).map(m => ({ role: m.role, content: m.content }))
      };

      // Submit task (returns immediately with task_id)
      const response = await fetch(`${API_URL}/healthcare-twins/collective`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          navigate('/auth');
          return;
        }
        throw new Error('Failed to start healthcare chat');
      }

      const data = await response.json();
      const taskId = data.task_id;
      
      if (!taskId) {
        throw new Error('No task ID received');
      }
      
      // Start polling for status every 2 seconds
      pollIntervalRef.current = setInterval(() => {
        pollTaskStatus(taskId);
      }, 2000);
      
      // Also poll immediately
      pollTaskStatus(taskId);

    } catch (error) {
      console.error('Healthcare chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error starting the consultation. Please try again.' 
      }]);
      setIsLoading(false);
    }
  }, [input, isLoading, messages, pollTaskStatus, navigate]);

  // ── Plan review (result.plan_review) ──────────────────────────────
  // Approve = run the plan exactly as shown; edits = a normal chat message
  // carrying the composed change description (the backend resumes/replans).
  // Plan-review ETA pinning: the run banner shows the approved plan's ETA only
  // for runs actually started from the card (see handlePlanApprove).
  const [approvedPlanReview, setApprovedPlanReview] = useState<PlanReviewPayload | null>(null);
  const lastPlanReviewRef = useRef<PlanReviewPayload | null>(null);

  const handlePlanApprove = useCallback(() => {
    // Pin the approved plan's ETA to THIS run — the last assistant message's
    // plan_review would show a stale estimate if the user typed a new question instead.
    setApprovedPlanReview(lastPlanReviewRef.current);
    void handleSend(APPROVE_PLAN_MESSAGE);
  }, [handleSend]);
  const handlePlanRequestChanges = useCallback((message: string) => {
    setApprovedPlanReview(null);
    void handleSend(message);
  }, [handleSend]);
  const focusComposer = useCallback(() => { inputRef.current?.focus(); }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Show access denied for non-admin users
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FEFEFE] flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-gray-100 px-6 py-4">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
                <Heart className="w-5 h-5" />
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">Ask AI Moms</h1>
              </div>
            </div>
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <img src={`${PUBLIC_URL}/logo.png`} alt="BluePill Logo" className="h-24" />
            </a>
          </div>
        </header>

        {/* Access Denied Content */}
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Admin Access Required</h2>
            <p className="text-gray-500 mb-6">
              This feature is only available to administrators. The Ask AI Moms chat provides access to sensitive Labor & Delivery experience personas data and is restricted to authorized personnel only.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl font-medium transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Go to Dashboard
              </Link>
              <Link
                to="/ask-collective/chat"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 hover:border-[#07E5D1] text-gray-700 hover:text-[#07E5D1] rounded-xl font-medium transition-colors"
              >
                <Users className="w-4 h-4" />
                Try Ask Collective
              </Link>
            </div>
            <p className="text-xs text-gray-400 mt-6">
              If you believe you should have access, please contact your administrator.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Plan-derived ETA for the in-flight run: prefer a live structured_progress
  // ETA; else the ETA of the plan the user just approved (the plan card is the
  // latest assistant message exactly when the current run was started from it).
  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
  const runEta = resolveRunEta(structuredProgress, approvedPlanReview ?? undefined);
  lastPlanReviewRef.current = lastAssistantMsg?.plan_review ?? null;
  // Track run start + tick every 30s while loading so the ETA copy can flip to an overrun
  // state once elapsed passes ~1.5× the estimate (otherwise "Estimated 6–8 min" sits there
  // silently at 24m elapsed). The poll re-renders on progress, but a run can overrun with no
  // new progress, so drive an independent tick.
  const [, setEtaTick] = useState(0);
  useEffect(() => {
    if (!isLoading) { runStartedAtRef.current = null; return; }
    if (runStartedAtRef.current == null) runStartedAtRef.current = Date.now();
    const id = setInterval(() => setEtaTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, [isLoading]);
  const runElapsedMs = isLoading && runStartedAtRef.current != null
    ? Date.now() - runStartedAtRef.current
    : undefined;
  const runEtaCopy = planEtaRunCopy(runEta?.eta_minutes_min, runEta?.eta_minutes_max, runElapsedMs);

  return (
    <div className="min-h-screen bg-[#FEFEFE] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#07E5D1] flex items-center justify-center text-white">
              <Heart className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-gray-900">Ask AI Moms</h1>
                <span className="px-2 py-0.5 bg-[#E8FCFA] text-[#07E5D1] text-xs font-medium rounded-full flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" />
                  Admin Only
                </span>
              </div>
              <p className="text-xs text-gray-500">Labor & Delivery Experience</p>
            </div>
          </div>

          {/* Logo - links to home */}
          <a
            href="/"
            className="flex items-center hover:opacity-80 transition-opacity"
          >
            <img
              src={`${PUBLIC_URL}/logo.png`}
              alt="BluePill Logo"
              className="h-24"
            />
          </a>
        </div>
      </header>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-6 py-6">
          {/* Empty state */}
          {messages.length === 0 && !streamingContent && (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-full bg-[#07E5D1] flex items-center justify-center text-white mx-auto mb-6">
                <Heart className="w-10 h-10" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Ask AI Moms</h2>
              <p className="text-gray-500 max-w-lg mx-auto mb-2">
                Ask any question about Labor & Delivery experience and our AI will select relevant Persona respondents, gather their perspectives, and synthesize insights.
              </p>
              <p className="text-sm text-[#07E5D1] font-medium mb-8">
                Labor & Delivery Experience
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {[
                  'What factors influenced hospital selection?',
                  'How did respondents feel about their care quality?',
                  'What were common pain points during delivery?'
                ].map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setInput(suggestion)}
                    className="px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors shadow-sm"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="space-y-6">
            {messages.map((msg, idx) => (
              <div key={idx}>
                <div className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && msg.format === PLAN_REVIEW_FORMAT && (msg.plan_review?.steps?.length ?? 0) > 0 ? (
                    // The plan card replaces the bubble (msg.content is a markdown
                    // fallback of the same plan). Only the LATEST message is
                    // actionable; older cards render read-only — superseded plans.
                    <PlanReviewCard
                      steps={msg.plan_review!.steps}
                      etaMinutesMin={msg.plan_review!.eta_minutes_min}
                      etaMinutesMax={msg.plan_review!.eta_minutes_max}
                      onApprove={handlePlanApprove}
                      onRequestChanges={handlePlanRequestChanges}
                      onFocusComposer={focusComposer}
                      disabled={isLoading || idx !== messages.length - 1}
                    />
                  ) : (
                  <div
                    className={`rounded-2xl px-5 py-4 ${
                      msg.role === 'user'
                        ? 'max-w-[75%] bg-[#07E5D1] text-white rounded-br-md'
                        : 'w-full bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <>
                        <div className="max-w-none">
                          <ReactMarkdown components={markdownComponents}>{msg.content}</ReactMarkdown>
                        </div>
                        {msg.experts && msg.experts.length > 0 && (
                          <ExpertsAccordion experts={msg.experts} />
                        )}
                      </>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                  )}
                </div>
              </div>
            ))}

            {/* Streaming content */}
            {streamingContent && (
              <div className="flex justify-start">
                <div className="w-full rounded-2xl px-5 py-4 bg-white border border-gray-200 text-gray-800 rounded-bl-md shadow-sm">
                  <div className="max-w-none">
                    <ReactMarkdown components={markdownComponents}>{streamingContent}</ReactMarkdown>
                  </div>
                </div>
              </div>
            )}

            {/* Loading indicator with structured progress */}
            {isLoading && !streamingContent && (
              <div className="flex justify-start">
                {structuredProgress ? (
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md shadow-sm w-full max-w-xl overflow-hidden">
                    {/* Header with overall progress */}
                    <div className="bg-gradient-to-r from-[#07E5D1]/10 to-[#E8FCFA] px-5 py-3 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-[#07E5D1]" />
                          <span className="text-sm font-semibold text-gray-800">
                            {structuredProgress.message || 'Processing'}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          {Math.round((structuredProgress.stages.filter(s => s.status === 'completed').length / structuredProgress.stages.length) * 100)}% complete
                        </span>
                      </div>
                      {/* Mini progress bar */}
                      <div className="mt-2 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#07E5D1] rounded-full transition-all duration-500 ease-out"
                          style={{ width: `${Math.round((structuredProgress.stages.filter(s => s.status === 'completed').length / structuredProgress.stages.length) * 100)}%` }}
                        />
                      </div>
                      {/* Plan-derived ETA — only shown when a reviewed plan (or the
                          backend's live progress) supplies one. */}
                      {runEtaCopy && (
                        <p className="mt-1 text-[11px] font-medium text-[#0B7C74]">{runEtaCopy}</p>
                      )}
                    </div>

                    {/* Stage indicators - compact horizontal view */}
                    <div className="px-5 py-3 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        {structuredProgress.stages.map((stage, idx) => (
                          <React.Fragment key={stage.stage}>
                            <div className="flex flex-col items-center">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                                stage.status === 'completed'
                                  ? 'bg-[#07E5D1] text-white'
                                  : stage.status === 'active'
                                  ? 'bg-[#E8FCFA] border-2 border-[#07E5D1] text-[#07E5D1]'
                                  : 'bg-gray-100 text-gray-400'
                              }`}>
                                {stage.status === 'completed' ? (
                                  <CheckCircle2 className="w-4 h-4" />
                                ) : stage.status === 'active' ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <span className="text-xs font-medium">{idx + 1}</span>
                                )}
                              </div>
                              <span className={`text-[10px] mt-1 text-center max-w-[60px] leading-tight ${
                                stage.status === 'active' ? 'text-gray-700 font-medium' : 'text-gray-400'
                              }`}>
                                {stage.label.split(' ').slice(0, 2).join(' ')}
                              </span>
                            </div>
                            {idx < structuredProgress.stages.length - 1 && (
                              <div className={`flex-1 h-0.5 mx-1 rounded ${
                                stage.status === 'completed' ? 'bg-[#07E5D1]' : 'bg-gray-200'
                              }`} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {/* Plan steps as todo list */}
                    {structuredProgress.plan_steps && structuredProgress.plan_steps.length > 0 && (
                      <div className="px-5 py-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                            Plan {structuredProgress.plan_version > 1 && <span className="text-[#07E5D1]">(updated)</span>}
                          </span>
                          <span className="text-xs text-gray-400">
                            {structuredProgress.plan_steps.filter(s => s.status === 'completed').length}/{structuredProgress.plan_steps.length} done
                          </span>
                        </div>

                        <ul className="space-y-2">
                          {structuredProgress.plan_steps.map((step, idx) => (
                            <li
                              key={`${step.id}-${idx}`}
                              className={`transition-all duration-300 ${
                                step.status === 'running'
                                  ? 'bg-[#E8FCFA] rounded-lg p-3 -mx-2 border border-[#07E5D1]/20'
                                  : 'px-1'
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Status icon */}
                                <div className="flex-shrink-0 mt-0.5">
                                  {step.status === 'completed' && (
                                    <CheckCircle2 className="w-5 h-5 text-[#07E5D1]" />
                                  )}
                                  {step.status === 'running' && (
                                    <div className="relative">
                                      <Circle className="w-5 h-5 text-[#07E5D1]" />
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-ping" />
                                      </div>
                                    </div>
                                  )}
                                  {step.status === 'pending' && (
                                    <Circle className="w-5 h-5 text-gray-300" />
                                  )}
                                  {step.status === 'skipped' && (
                                    <Circle className="w-5 h-5 text-gray-300" />
                                  )}
                                </div>

                                {/* Label and progress */}
                                <div className="flex-1 min-w-0">
                                  <span className={`text-sm leading-tight block ${
                                    step.status === 'completed'
                                      ? 'text-gray-500 line-through'
                                      : step.status === 'running'
                                      ? 'text-gray-800 font-medium'
                                      : 'text-gray-500'
                                  }`}>
                                    {step.label}
                                  </span>

                                  {/* Progress bar for running steps */}
                                  {step.status === 'running' && (
                                    <div className="mt-2">
                                      <div className="h-1 bg-[#07E5D1]/20 rounded-full overflow-hidden">
                                        <div className="h-full bg-[#07E5D1] rounded-full animate-progress-indeterminate" />
                                      </div>
                                      <p className="text-[10px] text-[#07E5D1] mt-1 flex items-center gap-1">
                                        <Loader2 className="w-3 h-3 animate-spin" />
                                        Processing...
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ul>

                        {/* Activity summary */}
                        {structuredProgress.plan_steps.filter(s => s.status === 'running').length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2 text-xs text-gray-500">
                              <Loader2 className="w-3 h-3 animate-spin text-[#07E5D1]" />
                              <span>
                                {structuredProgress.plan_steps.filter(s => s.status === 'running').length === 1
                                  ? '1 task in progress'
                                  : `${structuredProgress.plan_steps.filter(s => s.status === 'running').length} tasks running in parallel`}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Current activity message */}
                    <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-[#07E5D1] rounded-full animate-pulse" />
                        <p className="text-xs text-gray-600 truncate">
                          {structuredProgress.message}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-5 py-4 shadow-sm">
                    <div className="flex items-center gap-3 mb-2">
                      <Loader2 className="w-5 h-5 animate-spin text-[#07E5D1]" />
                      <span className="text-sm font-medium text-gray-700">
                        {progressMessage}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 ml-8">
                      {/* Prefer the approved plan's ETA over the generic copy. */}
                      {runEtaCopy ?? 'This may take a moment'}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-gray-100 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about Labor & Delivery experiences..."
              disabled={isLoading}
              className="flex-1 px-5 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1] focus:bg-white transition-all disabled:bg-gray-100 disabled:text-gray-500"
            />
            <button
              onClick={() => handleSend()}
              disabled={!input.trim() || isLoading}
              className="p-3.5 bg-[#07E5D1] hover:bg-[#06D4C2] text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 text-center mt-3">
            Powered by AI • Insights from Labor & Delivery Experience
          </p>
        </div>
      </div>
    </div>
  );
};

export default AskHealthcareChat;

